import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  CloudUpload as ImportIcon,
  Refresh as ResetIcon
} from '@mui/icons-material';
import Papa from 'papaparse';

import ImportUploadStep from './ImportUploadStep';
import ImportMappingStep from './ImportMappingStep';
import ImportReviewStep from './ImportReviewStep';
import {
  checkDuplicateCounts,
  executePhysicalCountImport
} from '../../../services/inventoryService';

const STEPS = ['Upload CSV', 'Map Columns', 'Review & Import'];

/**
 * PhysicalCountImport - Main container for CSV import flow
 * Orchestrates the 3-step wizard: Upload → Map → Review/Import
 */
function PhysicalCountImport({ companyId, onImportComplete }) {
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: File upload
  const [file, setFile] = useState(null);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseError, setParseError] = useState(null);

  // Step 2: Parsed data & mapping
  const [parsedData, setParsedData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});

  // Step 3: Duplicate check & import
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  // Calculate validation summary from mapping
  const getValidationSummary = useCallback(() => {
    if (!parsedData || !columnMapping) return null;

    const requiredFields = ['sku', 'counted_quantity', 'warehouse_code', 'count_date'];
    const missingRequired = requiredFields.filter(f => !columnMapping[f]);

    if (missingRequired.length > 0) {
      return { valid: 0, errors: parsedData.rows.length, warnings: 0 };
    }

    let valid = 0;
    let errors = 0;
    let warnings = 0;

    parsedData.rows.forEach(row => {
      const sku = row[columnMapping.sku];
      const qty = row[columnMapping.counted_quantity];
      const warehouse = row[columnMapping.warehouse_code];
      const date = row[columnMapping.count_date];

      const hasAllRequired = sku && qty !== undefined && qty !== '' && warehouse && date;
      const qtyValid = !isNaN(parseFloat(qty)) && parseFloat(qty) >= 0;

      if (hasAllRequired && qtyValid) {
        valid++;
      } else {
        errors++;
      }
    });

    return { valid, errors, warnings };
  }, [parsedData, columnMapping]);

  // Handle file selection and parsing
  const handleFileSelected = useCallback((selectedFile) => {
    // Clear previous state
    setParseError(null);
    setParsedData(null);
    setColumnMapping({});
    setDuplicateCheck(null);
    setImportResult(null);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setParseLoading(true);

    // Parse CSV file
    Papa.parse(selectedFile, {
      header: true,
      // 'greedy' also drops rows that are only empty/whitespace cells (e.g. ",,,"),
      // which plain `true` keeps — those were showing up as phantom blank error rows.
      skipEmptyLines: 'greedy',
      complete: (results) => {
        setParseLoading(false);

        if (results.errors && results.errors.length > 0) {
          setParseError(`CSV parsing error: ${results.errors[0].message}`);
          return;
        }

        // Belt-and-suspenders: drop any row where every value is blank/whitespace
        // (covers quoted empties / BOM cases greedy can miss).
        const cleanedRows = (results.data || []).filter(row =>
          Object.values(row).some(v => v != null && String(v).trim() !== '')
        );

        if (cleanedRows.length === 0) {
          setParseError('CSV file is empty or has no data rows');
          return;
        }

        setParsedData({
          headers: results.meta.fields || [],
          rows: cleanedRows
        });

        // Auto-advance to mapping step
        setActiveStep(1);
      },
      error: (error) => {
        setParseLoading(false);
        setParseError(`Failed to read file: ${error.message}`);
      }
    });
  }, []);

  // Handle mapping change
  const handleMappingChange = useCallback((newMapping) => {
    setColumnMapping(newMapping);
  }, []);

  // Check for duplicates when entering review step
  const checkForDuplicates = useCallback(async () => {
    if (!parsedData || !columnMapping.warehouse_code || !columnMapping.count_date) {
      return;
    }

    setDuplicateLoading(true);

    try {
      // Build unique warehouse/date combinations
      const uniqueCombinations = new Map();
      parsedData.rows.forEach(row => {
        const warehouse = row[columnMapping.warehouse_code]?.toString().toUpperCase().trim();
        const date = row[columnMapping.count_date];
        if (warehouse && date) {
          const key = `${warehouse}_${date}`;
          uniqueCombinations.set(key, { warehouse_code: warehouse, count_date: date });
        }
      });

      const checks = Array.from(uniqueCombinations.values());

      if (checks.length > 0) {
        const result = await checkDuplicateCounts(checks, companyId);
        setDuplicateCheck(result);
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
      // Don't block import on duplicate check failure
    } finally {
      setDuplicateLoading(false);
    }
  }, [parsedData, columnMapping, companyId]);

  // Execute import
  const handleImport = useCallback(async () => {
    if (!parsedData || !columnMapping) return;

    setImportLoading(true);
    setImportError(null);
    setImportProgress({ current: 0, total: parsedData.rows.length, percentage: 0 });

    try {
      // Transform data according to mapping
      const transformedRows = parsedData.rows.map(row => ({
        sku: row[columnMapping.sku]?.toString().trim(),
        counted_quantity: parseFloat(row[columnMapping.counted_quantity]) || 0,
        warehouse_code: row[columnMapping.warehouse_code]?.toString().toUpperCase().trim(),
        count_date: row[columnMapping.count_date],
        batch_number: columnMapping.batch_number ? row[columnMapping.batch_number] : null,
        gtin: columnMapping.gtin ? row[columnMapping.gtin] : null,
        asin: columnMapping.asin ? row[columnMapping.asin] : null,
        expiry_date: columnMapping.expiry_date ? row[columnMapping.expiry_date] : null,
        unit_cost: columnMapping.unit_cost ? parseFloat(row[columnMapping.unit_cost]) || null : null
      }));

      // Filter out invalid rows
      const validRows = transformedRows.filter(row =>
        row.sku && row.warehouse_code && row.count_date &&
        !isNaN(row.counted_quantity) && row.counted_quantity >= 0
      );

      const result = await executePhysicalCountImport({
        rows: validRows,
        company_id: companyId,
        source: 'csv_import',
        original_filename: file?.name
      });

      // Warehouse/date groups the server refused (DT-864) are listed, so a partial import is never reported as complete.
      const refused = (Array.isArray(result.refused) ? result.refused : [])
        .map(g => `${g.warehouse_code} ${g.count_date}: ${g.error}`);
      const anyImported = (result.countsCreated || 0) > 0;
      setImportResult({
        success: anyImported || refused.length === 0,
        message: anyImported || refused.length === 0
          ? `Successfully imported ${result.countsCreated || 0} physical count(s)`
          : 'Nothing was imported',
        errors: refused,
        refused,
        summary: {
          countsCreated: result.countsCreated || 0,
          itemsCreated: result.itemsCreated || 0,
          adjustmentsCreated: result.adjustmentsCreated || 0,
          skipped: parsedData.rows.length - validRows.length
        }
      });

      // onImportComplete switches to Count History, which would hide the refused list: stay on the result instead.
      if (onImportComplete && refused.length === 0) {
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Import failed:', error);
      // The server's own reason (e.g. an undecidable date order, DT-1634) beats axios's "status code 400".
      const reason = error.response?.data?.error || error.message || 'Import failed';
      setImportResult({
        success: false,
        message: reason,
        errors: [reason]
      });
    } finally {
      setImportLoading(false);
      setImportProgress(null);
    }
  }, [parsedData, columnMapping, companyId, file, onImportComplete]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (activeStep === 1) {
      // Moving to review step - check duplicates
      checkForDuplicates();
    }
    setActiveStep(prev => Math.min(prev + 1, STEPS.length - 1));
  }, [activeStep, checkForDuplicates]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setParsedData(null);
    setColumnMapping({});
    setParseError(null);
    setDuplicateCheck(null);
    setImportResult(null);
    setImportError(null);
  }, []);

  // Check if can proceed to next step
  const canProceed = useCallback(() => {
    switch (activeStep) {
      case 0:
        return parsedData !== null && !parseError;
      case 1: {
        // Check all required fields are mapped
        const requiredFields = ['sku', 'counted_quantity', 'warehouse_code', 'count_date'];
        const allMapped = requiredFields.every(f => columnMapping[f]);
        const validation = getValidationSummary();
        return allMapped && validation && validation.valid > 0;
      }
      case 2:
        return !importLoading && !importResult;
      default:
        return false;
    }
  }, [activeStep, parsedData, parseError, columnMapping, getValidationSummary, importLoading, importResult]);

  // Render current step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ImportUploadStep
            file={file}
            onFileSelected={handleFileSelected}
            loading={parseLoading}
            error={parseError}
          />
        );
      case 1:
        return (
          <ImportMappingStep
            parsedData={parsedData}
            mapping={columnMapping}
            onMappingChange={handleMappingChange}
            loading={parseLoading}
            error={parseError}
          />
        );
      case 2:
        return (
          <ImportReviewStep
            parsedData={parsedData}
            mapping={columnMapping}
            duplicateCheck={duplicateCheck}
            validationSummary={getValidationSummary()}
            loading={duplicateLoading}
            error={importError}
            importProgress={importProgress}
            importResult={importResult}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box>
            {activeStep > 0 && !importResult && (
              <Button
                onClick={handleBack}
                startIcon={<BackIcon />}
                disabled={importLoading}
              >
                Back
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {importResult && (
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ResetIcon />}
              >
                Import Another File
              </Button>
            )}

            {activeStep < STEPS.length - 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NextIcon />}
                disabled={!canProceed()}
              >
                Next
              </Button>
            )}

            {activeStep === STEPS.length - 1 && !importResult && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
                startIcon={<ImportIcon />}
                disabled={!canProceed() || importLoading}
              >
                {importLoading ? 'Importing...' : 'Import Now'}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default PhysicalCountImport;
